// @dynamic-i18n-keys
// Dynamic t() at lines 93–106 (postType: PostsType | 'featuredJournal' | 'featuredPosts'):
//   pages.posts.emptyState.all.title.owner
//   pages.posts.emptyState.all.title.visitor
//   pages.posts.emptyState.all.subtitle.owner
//   pages.posts.emptyState.all.subtitle.visitor
//   pages.posts.emptyState.all.createCTA
//   pages.posts.emptyState.journals.title.owner
//   pages.posts.emptyState.journals.title.visitor
//   pages.posts.emptyState.journals.subtitle.owner
//   pages.posts.emptyState.journals.subtitle.visitor
//   pages.posts.emptyState.journals.createCTA
//   pages.posts.emptyState.statuses.title.owner
//   pages.posts.emptyState.statuses.title.visitor
//   pages.posts.emptyState.statuses.subtitle.owner
//   pages.posts.emptyState.statuses.subtitle.visitor
//   pages.posts.emptyState.statuses.createCTA
//   pages.posts.emptyState.polls.title.owner
//   pages.posts.emptyState.polls.title.visitor
//   pages.posts.emptyState.polls.subtitle.owner
//   pages.posts.emptyState.polls.subtitle.visitor
//   pages.posts.emptyState.polls.createCTA
//   pages.posts.emptyState.drafts.title.owner
//   pages.posts.emptyState.drafts.title.visitor
//   pages.posts.emptyState.drafts.subtitle.owner
//   pages.posts.emptyState.drafts.subtitle.visitor
//   pages.posts.emptyState.drafts.createCTA
//   pages.posts.emptyState.featuredJournal.title.owner
//   pages.posts.emptyState.featuredJournal.title.visitor
//   pages.posts.emptyState.featuredJournal.subtitle.owner
//   pages.posts.emptyState.featuredJournal.subtitle.visitor
//   pages.posts.emptyState.featuredJournal.createCTA
//   pages.posts.emptyState.featuredPosts.title.owner
//   pages.posts.emptyState.featuredPosts.title.visitor
//   pages.posts.emptyState.featuredPosts.subtitle.owner
//   pages.posts.emptyState.featuredPosts.subtitle.visitor
//   pages.posts.emptyState.featuredPosts.createCTA
import type { FC } from 'react';
import { useCallback } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-papi-types';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import EmptyState from '../../../../EmptyState';
import { useVerification } from '@wix/da-shared-react/pkg/VerificationPopup/withVerification';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { VerificationRequiredActions } from '@wix/da-shared-react/pkg/publicSession/types';
import noop from '@wix/da-shared-react/pkg/utils/noop';
import { PostsType } from '../../../../../../types/posts';
import { CTAModalsMap } from './constants';
import { AddNewContentClickBiEvent, BiData } from '@wix/da-bi/pkg/events';

import s from './EmptyState.scss';

const POST_EMPTY_ILLUSTRATION_URL = 'https://st.deviantart.net/eclipse/illustrations/post.png';

export interface Props {
  postType: PostsType | 'featuredJournal' | 'featuredPosts';
  view?: 'large' | 'small' | 'wide';
  isOwner: boolean;
  profileOwner: PapiUser;
  className?: string;
  openSubmitPostPopup?: (modalProps?: any) => void;
}

function getVerificationPopupType(postType: Props['postType']): VerificationRequiredActions {
  switch (postType) {
    case 'journals':
    case 'drafts':
      return 'submitjournal';
    case 'polls':
      return 'submitpoll';
    case 'statuses':
      return 'submitstatus';
    default:
      return 'submit';
  }
}

const PostsEmptyState: FC<Props> = ({
  view,
  isOwner,
  profileOwner,
  postType,
  className,
  openSubmitPostPopup = noop,
}) => {
  const { t } = useTranslation();
  const { isVerificationNeeded, pushVerificationPopup } = useVerification();
  const logBiEvent = useBiLogger();

  const modalType = CTAModalsMap[postType];

  const handleButtonClick = useCallback(() => {
    if (!modalType) {
      return null;
    }

    if (isVerificationNeeded) {
      pushVerificationPopup(getVerificationPopupType(postType));
    } else {
      openSubmitPostPopup({
        groupDestination: profileOwner.isGroup && profileOwner,
      });
      logBiEvent(
        BiData<AddNewContentClickBiEvent>({
          evid: 284,
          widgetname: `${postType}_emptystate`,
        })
      );
    }
  }, [
    isVerificationNeeded,
    logBiEvent,
    modalType,
    openSubmitPostPopup,
    postType,
    pushVerificationPopup,
    profileOwner,
  ]);

  // New empty state for owner with feature flag (only for main section, not sidebar)
  const showNewOwnerEmptyState = isOwner && view !== 'small';

  if (showNewOwnerEmptyState) {
    return (
      <EmptyState
        view={view}
        className={classnames(
          s['root'],
          s['new-empty-state'],
          view === 'large' && s['large'],
          className
        )}
        backgroundType="none"
        title={t('pages.posts.emptyState.title.owner.v2')}
        titleClassName={s['new-title']}
        subtitle={t('pages.posts.emptyState.subtitle.owner.v2')}
        subtitleClassName={s['new-subtitle']}
        illustration={
          <img src={POST_EMPTY_ILLUSTRATION_URL} alt="" className={s['new-illustration']} />
        }
        customButton={
          modalType && (
            <BrandedButton
              variant="brand-green"
              size="medium"
              onClick={handleButtonClick}
              biData={BiData<AddNewContentClickBiEvent>({
                evid: 284,
                widgetname: `${postType}_emptystate`,
              })}
            >
              {t('pages.posts.emptyState.createCTA.v2')}
            </BrandedButton>
          )
        }
      />
    );
  }

  return (
    <EmptyState
      view={view}
      className={classnames(s['root'], view === 'large' && s['large'], className)}
      title={
        isOwner
          ? t(`pages.posts.emptyState.${postType}.title.owner`)
          : t(`pages.posts.emptyState.${postType}.title.visitor`, {
              username: profileOwner.username,
            })
      }
      subtitle={
        isOwner
          ? t(`pages.posts.emptyState.${postType}.subtitle.owner`)
          : t(`pages.posts.emptyState.${postType}.subtitle.visitor`, {
              username: profileOwner.username,
            })
      }
      buttonLabel={modalType && t(`pages.posts.emptyState.${postType}.createCTA`)}
      onButtonClick={handleButtonClick}
      withButton={isOwner && modalType}
    />
  );
};
PostsEmptyState.displayName = 'PostsEmptyState';

export default PostsEmptyState;
